import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import type {ColumnDef} from "@tanstack/react-table";
import type {Audience} from "crqlar-react-common/dist/models/audience/audience.model";
import {DataFilterOperator, Filter, FilterGroup} from "crqlar-react-common/dist/models/audience/filter.model";
import {MixpanelService} from "crqlar-react-common/dist/services/analytics/mixpanel.service";
import {AudienceService} from "crqlar-react-common/dist/services/audience/audience.service";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import {useAudienceFilterOptions} from "crqlar-react-common/dist/utils/hooks/audience/useAudience";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";
import {useCallback, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {useQueryClient} from "react-query";
import {
    RightSidebarCreateButton,
    RightSidebarDivider,
    RightSidebarRail,
    RightSidebarRailLayout,
} from "../layout/rightSidebar/RightSidebarRail";
import {
    ServerTable,
    ServerTableFrame,
    ServerTableProvider,
    ServerTableRailColumns,
    ServerTableRailFilter,
    ServerTableRailFullscreen,
    ServerTableRailSearch,
    ServerTableRailSort,
    useServerTable,
} from "../common/serverTable";
import {DateElement} from "../common/uiElements/DateElement";
import {SimpleChipElement} from "../common/uiElements/SimpleChipElement";
import AudienceDialog from "./AudienceDialog";

function AudienceLastSyncCell({syncData}: {syncData?: Audience["syncData"]}) {
    const {t} = useTranslation();

    if (!syncData?.length) return null;

    return (
        <Stack gap={0.75} sx={{py: 0.5}}>
            {syncData.map(sync => (
                <Box
                    key={sync.channel}
                    sx={{display: "flex", alignItems: "center", gap: 0.5}}
                >
                    <Box component="span" sx={{fontWeight: 500}}>
                        {sync.channel}:
                    </Box>
                    {sync.lastSync ? (
                        <DateElement date={sync.lastSync}/>
                    ) : (
                        <Box component="span" sx={{color: "text.secondary", fontStyle: "italic"}}>
                            {t("Not yet synced")}
                        </Box>
                    )}
                </Box>
            ))}
        </Stack>
    );
}

/** Rules in a filter, sub-groups included: what the list shows so an audience's complexity is visible at a glance. */
const countRules = (filter?: Filter): number => {
    const inGroup = (group: FilterGroup): number =>
        (group.filterRules?.length ?? 0) + (group.subGroups ?? []).reduce((sum, sub) => sum + inGroup(sub), 0)
    return (filter?.filterGroups ?? []).reduce((sum, group) => sum + inGroup(group), 0)
}

const AudiencesList = () => {
    useTrackScreenVisit("audiences");

    const {t, i18n} = useTranslation();
    const queryClient = useQueryClient();
    const readOnlyUser = useReadOnly();
    const filterOptionsQuery = useAudienceFilterOptions();
    const [currentAudience, setCurrentAudience] = useState<Audience>();
    const numberFormatter = useMemo(
        () => new Intl.NumberFormat(i18n.resolvedLanguage || i18n.language),
        [i18n.language, i18n.resolvedLanguage],
    );

    const refreshTable = useCallback(
        () => queryClient.invalidateQueries(["server-table", "marketing-audiences"]),
        [queryClient],
    );
    const handleCreate = useCallback(() => {
        MixpanelService.getInstance().track("create audience");
        setCurrentAudience({});
    }, []);

    const columns = useMemo<ColumnDef<Audience>[]>(() => [
        {
            id: "name",
            header: t("Name"),
            accessorFn: audience => audience.name,
            cell: ({row}) => <Box>{row.original.name ?? null}</Box>,
            size: 280,
            meta: {defaultActive: true, isServerSortable: true, grow: true},
        },
        {
            id: "connector",
            header: t("Connector"),
            accessorFn: audience => audience.syncData?.map(sync => sync.channel),
            cell: ({row}) => (
                <SimpleChipElement values={row.original.syncData?.map(sync => sync.channel)}/>
            ),
            size: 260,
            meta: {
                defaultActive: true,
                isServerSortable: true,
                serverSortField: "syncData.channel",
            },
        },
        {
            id: "rules",
            header: t("Rules"),
            accessorFn: audience => countRules(audience.filter),
            cell: ({row}) => {
                const rules = countRules(row.original.filter)
                const boxes = row.original.filter?.filterGroups?.length ?? 0
                if (!rules) return <Box>{t('No rules')}</Box>
                const parts = [rules === 1 ? t('1 rule') : t('{{n}} rules', {n: rules})]
                if (boxes > 1) parts.push(boxes === 1 ? t('1 box') : t('{{n}} boxes', {n: boxes}))
                return <Box>{parts.join(' · ')}</Box>
            },
            size: 150,
            // Computed on the client from the loaded filter, so the server cannot sort by it.
            meta: {defaultActive: true, isServerSortable: false},
        },
        {
            id: "audienceSize",
            header: t("Audience Size"),
            accessorFn: audience => audience.audienceSize,
            cell: ({row}) => (
                <Box sx={{fontVariantNumeric: "tabular-nums"}}>
                    {row.original.audienceSize === undefined
                        ? null
                        : numberFormatter.format(row.original.audienceSize)}
                </Box>
            ),
            size: 160,
            meta: {defaultActive: true, isServerSortable: true},
        },
        {
            id: "marketingAudienceSize",
            header: t("Marketing Audience Size"),
            accessorFn: audience => audience.marketingAudienceSize,
            cell: ({row}) => (
                <Box sx={{fontVariantNumeric: "tabular-nums"}}>
                    {row.original.marketingAudienceSize === undefined
                        ? null
                        : numberFormatter.format(row.original.marketingAudienceSize)}
                </Box>
            ),
            size: 200,
            meta: {defaultActive: true, isServerSortable: true},
        },
        {
            id: "lastSync",
            header: t("Last Sync"),
            accessorFn: audience => audience.syncData?.map(sync => sync.lastSync),
            cell: ({row}) => <AudienceLastSyncCell syncData={row.original.syncData}/>,
            size: 280,
            meta: {
                defaultActive: true,
                isServerSortable: true,
                serverSortField: "syncData.lastSync",
            },
        },
        {
            id: "id",
            header: t("ID"),
            accessorFn: audience => audience.id,
            cell: ({row}) => <Box>{row.original.id ?? null}</Box>,
            size: 260,
            meta: {defaultActive: false, isServerSortable: true},
        },
        {
            id: "insertDate",
            header: t("Created"),
            accessorFn: audience => audience.insertDate,
            cell: ({row}) => <DateElement date={row.original.insertDate}/>,
            size: 200,
            meta: {defaultActive: false, isServerSortable: true},
        },
        {
            id: "updateDate",
            header: t("Last updated"),
            accessorFn: audience => audience.updateDate,
            cell: ({row}) => <DateElement date={row.original.updateDate}/>,
            size: 200,
            meta: {defaultActive: true, isServerSortable: true},
        },
    ], [numberFormatter, refreshTable, t]);

    const controller = useServerTable<Audience>({
        tableId: "marketing-audiences",
        columns,
        getRowId: audience => audience.id!,
        onRowClick: audience => setCurrentAudience(audience),
        initialSorting: [{id: "id", desc: true}],
        refetchInterval: 5 * 1000,
        queryFn: async ({pagination, searchQuery, filterRules, sorting}) => {
            const page = await AudienceService.getAllAudiences({
                query: searchQuery || undefined,
                advancedFilter: filterRules.length
                    ? {
                        filterGroups: [{
                            filterRules,
                            operator: DataFilterOperator.AND,
                        }],
                    }
                    : undefined,
                limit: {
                    offset: pagination.pageIndex * pagination.pageSize,
                    limit: pagination.pageSize,
                    sort: sorting ? [sorting] : undefined,
                },
            });

            return {
                content: page.content ?? [],
                totalElements: page.totalElements ?? 0,
            };
        },
    });

    return (
        <>
            <Box sx={{display: "flex", flex: 1, minWidth: 0, minHeight: 0}}>
                <ServerTableProvider controller={controller}>
                    <ServerTableFrame>
                        <RightSidebarRailLayout>
                            <ServerTable/>

                            <RightSidebarRail>
                                <ServerTableRailSearch/>
                                <ServerTableRailSort/>
                                <ServerTableRailFilter
                                    options={filterOptionsQuery.data ?? []}
                                    disabled={filterOptionsQuery.isLoading || filterOptionsQuery.isError}
                                />
                                <ServerTableRailFullscreen/>
                                <ServerTableRailColumns/>
                                <RightSidebarDivider/>
                                <RightSidebarCreateButton
                                    label={t("Create audience")}
                                    disabled={readOnlyUser}
                                    onClick={handleCreate}
                                />
                            </RightSidebarRail>
                        </RightSidebarRailLayout>
                    </ServerTableFrame>
                </ServerTableProvider>
            </Box>

            <AudienceDialog
                key={currentAudience ? currentAudience.id ?? "new" : "closed"}
                audience={currentAudience}
                open={!!currentAudience}
                onClose={() => setCurrentAudience(undefined)}
                onSaved={() => {
                    setCurrentAudience(undefined);
                    void refreshTable();
                }}
            />
        </>
    );
};

export default AudiencesList;
